import { FileItem, FileUploader, ParsedResponseHeaders } from 'ng2-file-upload';

import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlContainer, NgForm, NgModel } from '@angular/forms';

import { DraftDepotRequeteService } from '../../../depot-requete/draft-depot-requete.service';
import { ALLOWED_MIME_TYPES, FILE_NAME_INVALID_ERROR_CODE, MAX_FILE_EXCEEDED_ERROR_CODE } from '../../constants/filesConstraints.constants';
import { saveOrOpenRawFile } from '../../core/util';
import { ParametersService } from '../../services/parameters.service';
import { FileUploaderModel } from './file-uploader.model';
import { ErrorEnum } from '../../constants/error.constants';

@Component({
    selector: 'jhi-file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class FileUploaderComponent implements OnInit {
    @Input()
    inputName: string;

    @Input()
    label: string;

    @Input()
    id: string;

    @Input()
    required: boolean;

    @Input()
    disabled: boolean;

    @Input()
    file: FileUploaderModel = null;

    @Input()
    url: string;

    @Output()
    fileChange = new EventEmitter<FileUploaderModel>();

    @ViewChild('validationInput')
    validationInput: NgModel;

    uploader: FileUploader;

    hasFileOver = false;
    acceptMimeType = ALLOWED_MIME_TYPES.join(',');
    fileName: string;
    isUploading = false;

    maxFileSize: number;
    errorTrFonctionnel: boolean;

    tailleError = false;
    formatError = false;
    nomFichierError = false;
    techniqueError = false;

    draftDepotRequeteService: DraftDepotRequeteService;

    constructor(private injector: Injector, private parametersService: ParametersService) {
       this.draftDepotRequeteService = this.injector
            .get<DraftDepotRequeteService>(DraftDepotRequeteService);
    }

    ngOnInit() {
        this.uploader = new FileUploader({ url: this.url, itemAlias: 'file', removeAfterUpload: true });
        this.uploader.onAfterAddingFile = this.onAfterAddingFile.bind(this);
        this.uploader.onBeforeUploadItem = this.onBeforeUploadItem.bind(this);
        this.uploader.onCompleteItem = this.onCompleteItem.bind(this);
        this.uploader.onSuccessItem = this.onSuccessItem.bind(this);
        this.uploader.onErrorItem = this.onErrorItem.bind(this);
        this.parametersService.getParameters().subscribe(parameters => (this.maxFileSize = parameters.maxFileSize));
    }

    onSuccessItem(item: FileItem, jsonResponse: string) {
        const response = JSON.parse(jsonResponse);
        this.isUploading = false;
        this.validationInput.control.setErrors(null);
        this.file = new FileUploaderModel(item.file, response.fileApiId, response.fileSize);
        this.fileChange.emit(this.file);
    }

    onErrorItem(_item: FileItem, response: string, status: number) {
        console.log('onErrorItem | response', response);
        this.isUploading = false;
        if (status === 400 && JSON.parse(response).code === MAX_FILE_EXCEEDED_ERROR_CODE) {
            this.validationInput.control.setErrors({ invalid: true, taille: true });
            this.tailleError = true;
        }else if (status === 400 && JSON.parse(response).code === FILE_NAME_INVALID_ERROR_CODE) {
            this.validationInput.control.setErrors({ invalid: true, nomFichier: true });
            this.nomFichierError = true;
        }else {
            this.validationInput.control.setErrors({ invalid: true, technique: true });
            this.techniqueError = true;
        }
        this.validationInput.control.markAsTouched();
    }

    onBeforeUploadItem(_fileItem: FileItem): any {
        if (this.url.includes('/api/my-draft-requete')) {
            const newOptions = this.draftDepotRequeteService.addRequestHeaderToUploader(this.uploader.options);
            this.uploader.setOptions(newOptions);
        }
    }

    onCompleteItem(item: FileItem, _jsonResponse: string, _status: number, headers: ParsedResponseHeaders): any {
        if (item.url.includes('/api/my-draft-requete')) {
            this.injector
                .get<DraftDepotRequeteService>(DraftDepotRequeteService)
                .updateVersionWithResponseHeaderFromUploader(headers);
        }
    }

    onAfterAddingFile(item: FileItem) {
        let errors = this.validateFile(item);
        this.validationInput.control.markAsUntouched();

        if (Object.keys(errors).length !== 0) {
            this.fileName = item.file.name;
            this.validationInput.control.setErrors(errors);
            this.validationInput.control.markAsTouched();
            this.uploader.removeFromQueue(item);
        } else if (!this.isUploading) {
            // Check for isUploading == false to prevent upload of multiple files
            this.validationInput.control.setErrors({ inprogress: true });
            this.uploader.uploadItem(item);
            this.isUploading = true;
        }
    }

    validateFile(item: FileItem): Object {
        let errors = {};

        if (item.file.size > this.maxFileSize) {
            errors['taille'] = true;
            this.tailleError = true;
        }

        if (!item.file.type || ALLOWED_MIME_TYPES.indexOf(item.file.type) === -1) {
            errors['format'] = true;
            this.formatError = true;
        }

        if ((item.file.name.length !== this.countCodePoints(item.file.name))) {
            errors['nomFichier'] = true;
            this.nomFichierError = true;
        }
        return errors;
    }

    // get the code point length to see whether a string contains a non BMP (Basic Multilingual Plane) character.
    countCodePoints(str: string): number {
        const i = str[Symbol.iterator]();
        let count = 0;
        while (!i.next().done ) {
            ++count;
        }
        return count;
    }

    isInvalid(): boolean {
        return !!this.validationInput && this.validationInput.invalid && this.validationInput.touched;
    }

    markAsUntouched() {
        this.validationInput.control.markAsUntouched();
    }

    fileOver(event) {
        this.hasFileOver = event;
    }

    viewFile() {
        // If the file is loaded we just open the file but in case the file is not loaded (if it's a draft version) we call FileApi
        if (File.prototype.isPrototypeOf(this.file.fileItem.rawFile)) {
            saveOrOpenRawFile(this.file.fileItem.rawFile, this.file.fileItem.name);
        } else {
            this.draftDepotRequeteService.downloadJustificatif(this.file.fileApiId)
                .subscribe(blob => saveOrOpenRawFile(blob, this.file.fileItem.name),
                error => {
                    if (error.errorType === ErrorEnum.TR_FUNCTIONAL_ERROR) {
                        this.errorTrFonctionnel = true;
                }});
        }
    }

    deleteFile() {
        this.file = undefined;
        this.fileChange.emit(this.file);
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/shared/components/file-uploader/file-uploader.component.ts